import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { useTranslations } from 'next-intl';
import { useCheckoutSelectAddressMachine } from '@context/checkoutCartMachineContext';
import {
  SELECT_TIME_SLOTS_STATES,
  SELECT_TIME_SLOTS_TRANSITIONS,
} from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectTimeSlots';
import { formatDateToPart } from '@monofront/business/utils';
import useScrollToRefOnMount from '@hooks/useScrollToRefOnMount';

const SelectTimeSlot: React.FC = () => {
  const { selectTimeSlotsState, sendToSelectTimeSlots } =
    useCheckoutSelectAddressMachine();

  const { timeSlots, daySelected, hourSelected } = selectTimeSlotsState.context;

  const selectedDaySlot = timeSlots.find(({ date }) => date === daySelected);

  const tShipping = useTranslations('Shipping');

  const timeSlotsRef = useScrollToRefOnMount();

  if (selectTimeSlotsState.matches(SELECT_TIME_SLOTS_STATES.SLOTS_FETCHING)) {
    return (
      <div className="loader-container">
        <HeartLoader />
      </div>
    );
  }

  return (
    <div
      style={{ scrollMarginTop: 'var(--checkout-header-height)' }}
      ref={timeSlotsRef}
    >
      <h3 className="subtitle hxb">{tShipping('chooseTimeSlot')}</h3>
      <h4 className="block-title">{tShipping('availableDates')}</h4>
      <div className="day-slots-container">
        {timeSlots.map((daySlot, index) => {
          const { day, month, weekday } = formatDateToPart(daySlot.date);
          const isAvailable = daySlot.slots.find(
            ({ availableCapacity }) => availableCapacity !== 0,
          );
          const isSelected = daySelected === daySlot.date;

          return (
            <button
              key={daySlot.date}
              onClick={() => {
                sendToSelectTimeSlots({
                  type: SELECT_TIME_SLOTS_TRANSITIONS.SELECT_DAY,
                  daySelected: daySlot.date,
                });
              }}
              className={`day-slot hxrt ${
                isSelected ? 'day-slot--selected' : ''
              }`}
              disabled={
                !isAvailable ||
                !selectTimeSlotsState.can({
                  type: SELECT_TIME_SLOTS_TRANSITIONS.SELECT_DAY,
                  daySelected: daySlot.date,
                })
              }
              data-testid={`CheckoutSelectShippingMethod-button-select-day-${
                index + 1
              }`}
            >
              <span>{weekday.replace('.', '')}</span>
              <span>{day}</span>
              <span>{month.replace('.', '')}</span>
            </button>
          );
        })}
      </div>

      {selectTimeSlotsState.can({
        type: SELECT_TIME_SLOTS_TRANSITIONS.SELECT_HOUR,
        hourSelected: '',
      }) && (
        <>
          <h4
            className="block-title"
            style={{ scrollMarginTop: 'var(--checkout-header-height)' }}
            ref={timeSlotsRef}
          >
            {tShipping('availableTimeSlots')}
          </h4>
          <div className="time-slots-container">
            {selectedDaySlot?.slots.map((slot, index) => {
              const isAvailable = slot.availableCapacity !== 0;
              const isSelected = slot.value === hourSelected;

              return (
                <button
                  key={slot.value}
                  className={`time-slot hxrt ${
                    isSelected ? 'time-slot--selected' : ''
                  }`}
                  disabled={!isAvailable}
                  onClick={() => {
                    sendToSelectTimeSlots({
                      type: SELECT_TIME_SLOTS_TRANSITIONS.SELECT_HOUR,
                      hourSelected: slot.value,
                    });
                  }}
                  data-testid={`CheckoutSelectShippingMethod-button-select-time-${
                    index + 1
                  }`}
                >
                  {slot.value}
                </button>
              );
            })}
          </div>
        </>
      )}
      <style jsx>{`
        .day-slots-container,
        .time-slots-container {
          display: inline-flex;
          flex-wrap: wrap;
          gap: var(--space-2);
        }

        .day-slot,
        .time-slot {
          border: 1px solid var(--clr-tertiary-2);
          background-color: transparent;
          box-sizing: border-box;
          border-radius: var(--rd-1);
          padding: var(--space-2);
          cursor: pointer;
        }

        .day-slot[disabled],
        .time-slot[disabled] {
          border-color: transparent;
          color: var(--clr-grey-4);
          cursor: not-allowed;
        }

        .day-slot--selected,
        .time-slot--selected {
          border-color: var(--clr-primary-5);
          background-color: var(--clr-primary-5);
          font-weight: var(--font-weight-bold);
          cursor: default;
        }

        .day-slot {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 62px;
          height: 64px;
          text-transform: capitalize;
          letter-spacing: -0.24px;
          color: var(--black);
        }

        .time-slot {
          width: 110px;
          letter-spacing: -0.33px;
          white-space: nowrap;
          color: var(--black);
        }
        .block-title {
          all: inherit;
          margin: var(--space-2) 0;
        }

        .loader-container {
          display: flex;
          justify-content: center;
        }

        @media (min-width: 1264px) {
          .day-slot {
            width: 110px;
          }
        }
      `}</style>
    </div>
  );
};

export default SelectTimeSlot;
